import React from 'react';
import { Accordion } from 'react-bootstrap';
import { content } from './FutureReadingContent';
import { useTranslation } from 'react-i18next';

const FutureReading = () => {
  const { i18n } = useTranslation();
  const lang = content[i18n.language];

  const styles = {
    container: {
      width: '100%',
      maxWidth: '100%',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      minHeight: '100vh',  // Add this if you want full viewport height
      paddingTop: 0,       // Ensure no top padding
      marginTop: 0
    },
    futureReading: {
      width: '100%',
      maxWidth: '1200px',
    },
    accordionItem: {
      borderLeft: 'none',
      borderRight: 'none',
    },
    accordionButton: {
      textAlign: 'left',
      width: '100%',
      backgroundColor: 'transparent',
      boxShadow: 'none',
      justifyContent: 'flex-start',
      paddingLeft: '1.25rem',
    },
    accordionBody: {
      width: '100%',
      padding: '1.5rem 1.25rem',
      textAlign: 'left',
    },
    accordionHeader: {
      textAlign: 'left',
      display: 'flex',
      justifyContent: 'flex-start',
      width: '100%',
    },
    heading: {
      textAlign: 'left',
      marginLeft: 0,
      marginRight: 'auto',
      fontWeight: 'bold',
    },
    description: {
      textAlign: 'left',
      marginLeft: 0,
      marginRight: 'auto',
    },
    sources: {
      textAlign: 'left',
      marginLeft: 0,
      marginRight: 'auto',
      paddingBottom: '0.5rem',
      fontWeight: 'bold'
    }
  };

  const renderInstitution = (inst, index) => (
    <div key={index} className="mb-4">
      <p style={styles.heading}>{inst.name}</p>
      {inst.description && <p style={styles.description}>{inst.description}</p>}

      <div className="ms-3">
        {inst.address && (
          <p className="mb-1 text-start">
            <strong>{i18n.language === 'lv' ? 'Adrese: ' : 'Address: '}</strong>
            {inst.address}
          </p>
        )}
        {inst.phone && (
          <p className="mb-1 text-start">
            <strong>{i18n.language === 'lv' ? 'Tālr.: ' : 'Tel.: '}</strong>
            {inst.phone}
          </p>
        )}
        {inst.email && (
          <p className="mb-1 text-start">
            <strong>E-{i18n.language === 'lv' ? 'pasts: ' : 'mail: '}</strong>
            <a href={`mailto:${inst.email}`}>{inst.email}</a>
          </p>
        )}
        {inst.website && (
          <p className="mb-1 text-start">
            <strong>{i18n.language === 'lv' ? 'Mājas lapa: ' : 'Homepage: '}</strong>
            <a href={inst.website} target="_blank" rel="noopener noreferrer">
              {inst.website}
            </a>
          </p>
        )}
        {inst.searchEngine && (
          <p className="mb-1 text-start">
            <strong>
              {i18n.language === 'lv'
                ? 'Online meklētājs (Meklēt LNA): '
                : 'Online search engine (Search LNA): '}
            </strong>
            <a href={inst.searchEngine} target="_blank" rel="noopener noreferrer">
              {inst.searchEngine}
            </a>
          </p>
        )}
        {inst.collections && (
          <div className="mt-3">
            <p className="mb-2 text-start">
              <strong>{i18n.language === 'lv' ? 'Noderīgi fondi:' : 'Useful collections:'}</strong>
            </p>
            {inst.collections.map((col, i) => (
              <p key={i} className="ms-3 mb-1 text-start">• {col}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderSection = (section, sIndex) => (
    <div key={sIndex} className="mb-4">
      <p style={styles.heading}>{section.subtitle}</p>

      <div className="ms-3">
        {section.address && (
          <p className="mb-1 text-start">
            <strong>{i18n.language === 'lv' ? 'Adrese: ' : 'Address: '}</strong>
            {section.address}
          </p>
        )}
        {section.phone && (
          <p className="mb-1 text-start">
            <strong>{i18n.language === 'lv' ? 'Tālr.: ' : 'Tel.: '}</strong>
            {section.phone}
          </p>
        )}
        {section.email && (
          <p className="mb-1 text-start">
            <strong>E-{i18n.language === 'lv' ? 'pasts: ' : 'mail: '}</strong>
            <a href={`mailto:${section.email}`}>{section.email}</a>
          </p>
        )}
        {section.website && (
          <p className="mb-1 text-start">
            <strong>{i18n.language === 'lv' ? 'Mājas lapa: ' : 'Website: '}</strong>
            <a href={section.website} target="_blank" rel="noopener noreferrer">
              {section.website}
            </a>
          </p>
        )}
        {section.searchEngine && (
          <p className="mb-1 text-start">
            <strong>{i18n.language === 'lv' ? 'Online meklētājs: ' : 'Online search engine: '}</strong>
            <a href={section.searchEngine} target="_blank" rel="noopener noreferrer">
              {section.searchEngine}
            </a>
          </p>
        )}
        {section.collections && (
          <div className="mt-3">
            <p className="mb-2 text-start">
              <strong>{i18n.language === 'lv' ? 'Noderīgi fondi:' : 'Useful collections:'}</strong>
            </p>
            {section.collections.map((col, i) => (
              <p key={i} className="ms-3 mb-1 text-start">• {col}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderMissingPersonsInstitution = (inst, index) => (
    <div key={index} className="mb-4">
      <h6 className="text-start mb-2">
        {index + 4}) {inst.name} {inst.note && <span className="text-muted fst-italic">{inst.note}</span>}
      </h6>

      <div className="ms-4">
        {inst.fullName && <p className="mb-2 text-start fst-italic">{inst.fullName}</p>}
        {inst.description && <p className="mb-2 text-start">{inst.description}</p>}

        {inst.address && (
          <p className="mb-1 text-start">
            <strong>{i18n.language === 'lv' ? 'Adrese: ' : 'Address: '}</strong>
            {inst.address}
          </p>
        )}
        {inst.phone && (
          <p className="mb-1 text-start">
            <strong>{i18n.language === 'lv' ? 'Tālrunis: ' : 'Phone: '}</strong>
            {inst.phone}
          </p>
        )}
        {inst.email && (
          <p className="mb-1 text-start">
            <strong>E-{i18n.language === 'lv' ? 'pasts: ' : 'mail: '}</strong>
            <a href={`mailto:${inst.email}`}>{inst.email}</a>
          </p>
        )}
        {inst.website && (
          <p className="mb-1 text-start">
            <strong>{i18n.language === 'lv' ? 'Mājas lapa: ' : 'Website: '}</strong>
            <a href={inst.website} target="_blank" rel="noopener noreferrer">
              {inst.website}
            </a>
          </p>
        )}
        {inst.websiteForm && (
          <p className="mb-1 text-start">
            <strong>
              {i18n.language === 'lv' ? 'Mājas lapa (iesnieguma forma): ' : 'Website (submission form): '}
            </strong>
            <a href={inst.websiteForm} target="_blank" rel="noopener noreferrer">
              {inst.websiteForm}
            </a>
          </p>
        )}
        {inst.searchEngine && (
          <p className="mb-1 text-start">
            <strong>{i18n.language === 'lv' ? 'Online meklētājs: ' : 'Online search engine: '}</strong>
            <a href={inst.searchEngine} target="_blank" rel="noopener noreferrer">
              {inst.searchEngine}
            </a>
          </p>
        )}
      </div>
    </div>
  );

  const renderMilitaryUnitsInstitution = (inst, index) => (
    <div key={index} className="mb-4">
      <h6 className="text-start mb-2">
        {inst.name} {inst.description && <span className="text-muted fst-italic">{inst.description}</span>}
      </h6>

      <div className="ms-4">
        {inst.fullName && <p className="mb-2 text-start fst-italic">{inst.fullName}</p>}

        {inst.address && (
          <p className="mb-1 text-start">
            <strong>{i18n.language === 'lv' ? 'Adrese: ' : 'Address: '}</strong>
            {inst.address}
          </p>
        )}
        {inst.email && (
          <p className="mb-1 text-start">
            <strong>E-{i18n.language === 'lv' ? 'pasts: ' : 'mail: '}</strong>
            <a href={`mailto:${inst.email}`}>{inst.email}</a>
          </p>
        )}
        {inst.website && (
          <p className="mb-1 text-start">
            <strong>{i18n.language === 'lv' ? 'Mājas lapa: ' : 'Website: '}</strong>
            <a href={inst.website} target="_blank" rel="noopener noreferrer">
              {inst.website}
            </a>
          </p>
        )}
        {inst.searchEngine && (
          <p className="mb-1 text-start">
            <strong>{i18n.language === 'lv' ? 'Online meklētājs: ' : 'Online search engine: '}</strong>
            <a href={inst.searchEngine} target="_blank" rel="noopener noreferrer">
              {inst.searchEngine}
            </a>
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.futureReading} className="text-start">
        <h2 className="mb-4 text-start">{lang.title}</h2>

        <Accordion defaultActiveKey="0" flush>
          {/* First World War Section */}
          <Accordion.Item eventKey="0" style={styles.accordionItem}>
            <Accordion.Header style={styles.accordionHeader}>
              <span style={styles.accordionButton}>{lang.wwi.title}</span>
            </Accordion.Header>
            <Accordion.Body style={styles.accordionBody} className="text-start">
              <p className="fw-bold mb-4">{lang.wwi.subtitle}</p>

              {lang.wwi.institutions.map((inst, index) => renderInstitution(inst, index))}

              <h5 style={styles.sources}>{lang.wwi.onlineLists.title}</h5>
              <div className="ms-3">
                {lang.wwi.onlineLists.items.map((item, index) => (
                  <p key={index} className="text-start mb-2">
                    {index + 1}) {item.name}
                    <br />
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="ms-3">
                      {item.url}
                    </a>
                  </p>
                ))}
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Second World War Section */}
          <Accordion.Item eventKey="1" style={styles.accordionItem}>
            <Accordion.Header style={styles.accordionHeader}>
              <span style={styles.accordionButton}>{lang.wwii.title}</span>
            </Accordion.Header>
            <Accordion.Body style={styles.accordionBody} className="text-start">
              <p className="fw-bold mb-4 text-start">{lang.wwii.subtitle}</p>

              {lang.wwii.institutions.map((inst, index) => (
                <div key={index} className="mb-4">
                  <h5 style={styles.heading}>{inst.name}</h5>

                  {inst.sections ? (
                    inst.sections.map((section, sIndex) => renderSection(section, sIndex))
                  ) : (
                    <div className="ms-3">
                      {inst.description && <p className="text-start mb-2">{inst.description}</p>}
                      {inst.address && (
                        <p className="mb-1 text-start">
                          <strong>{i18n.language === 'lv' ? 'Adrese: ' : 'Address: '}</strong>
                          {inst.address}
                        </p>
                      )}
                      {inst.phone && (
                        <p className="mb-1 text-start">
                          <strong>
                            {i18n.language === 'lv' ? 'Tālr.: ' : (inst.phone.includes('+49') ? 'Phone: ' : 'Tel.: ')}
                          </strong>
                          {inst.phone}
                        </p>
                      )}
                      {inst.email && (
                        <p className="mb-1 text-start">
                          <strong>E-{i18n.language === 'lv' ? 'pasts: ' : 'mail: '}</strong>
                          <a href={`mailto:${inst.email}`}>{inst.email}</a>
                        </p>
                      )}
                      {inst.website && (
                        <p className="mb-1 text-start">
                          <strong>{i18n.language === 'lv' ? 'Mājas lapa: ' : 'Website: '}</strong>
                          <a href={inst.website} target="_blank" rel="noopener noreferrer">
                            {inst.website}
                          </a>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* Missing Persons Section */}
              <h5 style={styles.heading}>{lang.wwii.missingPersons.title}</h5>
              <div className="mt-3">
                {lang.wwii.missingPersons.institutions.map((inst, index) =>
                  renderMissingPersonsInstitution(inst, index)
                )}
              </div>

              {/* Military Units Section */}
              <h5 style={styles.heading}>{lang.wwii.militaryUnits.title}</h5>
              <div className="mt-3">
                {lang.wwii.militaryUnits.institutions.map((inst, index) =>
                  renderMilitaryUnitsInstitution(inst, index)
                )}
              </div>

              {/* Soviet Service Section */}
              <h5 style={styles.heading}>{lang.wwii.sovietService.title}</h5>
              <h6 style={styles.sources}>{lang.wwii.sovietService.fallenMissing.title}</h6>
              <div className="ms-3">
                {lang.wwii.sovietService.fallenMissing.items.map((item, index) => (
                  <p key={index} className="text-start mb-2">
                    • {item.name && <strong>{item.name}</strong>}
                    {item.name && ' – '}
                    {item.description}
                    {item.url && (
                      <>
                        <br />
                        <a href={item.url} target="_blank" rel="noopener noreferrer" className="ms-3">
                          {item.url}
                        </a>
                      </>
                    )}
                  </p>
                ))}
              </div>

              <div className="mt-5 ms-3">
                <h6 style={styles.sources}>{lang.wwii.sovietService.searchEngines.title}</h6>
                {lang.wwii.sovietService.searchEngines.urls.map((url, index) => (
                  <p key={index} className="text-start mb-2">
                    • <a href={url} target="_blank" rel="noopener noreferrer">
                      {url}
                    </a>
                  </p>
                ))}
              </div>

              {/* Online Resources */}
              <div className="mt-5 ms-3">
                <h5 style={styles.sources}>{lang.wwii.onlineResources.title}</h5>
                {lang.wwii.onlineResources.items.map((item, index) => (
                  <p key={index} className="text-start mb-2">
                    • {item.name}
                    {item.url && (
                      <>
                        <br />
                        <a href={item.url} target="_blank" rel="noopener noreferrer" className="ms-3">
                          {item.url}
                        </a>
                      </>
                    )}
                  </p>
                ))}
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
        <p className="mt-5 fst-italic text-start border-top pt-3">{lang.preparedBy}</p>
      </div>
    </div>
  );
};

export default FutureReading;